import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import './index.scss';
import 'flag-icons/css/flag-icons.min.css';
// Initialise PostHog before App's module graph runs, so the first pageview is captured.
import '@host/config/posthog.ts';
import App from './App.tsx';
import '@host/config/aws-config.ts';
import { registerDebugTools } from '@host/utils/debugTools.ts';
import { getAppVersion } from '@/shared/utils/version.ts';
import { jitsiService } from '../../shared/singletons/jitsi-singleton.ts';
import { registerActivityCleanExit } from '@host/services/activity-clean-exit.service';
import { registerChatTracking } from '@host/utils/chat-tracking.ts';

// Capture ?rid= before React Router's ConditionalRedirect strips it from the URL.
const _pushRid = new URLSearchParams(window.location.search).get('rid');
if (_pushRid) sessionStorage.setItem('push_rid', _pushRid);

// A deploy's `s3 sync --delete` removes the previous build's hashed chunks, so a tab still
// running the old bundle fails the first time it lazy-loads one. Reload once to pick up the
// new build; the timestamp guard stops a reload loop if the chunk is genuinely missing.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'preload_error_reload_at';
  try {
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const appEnv = import.meta.env.VITE_ENVIRONMENT || 'development';
const isProd = appEnv === 'production';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || '', // Your Sentry DSN here
  environment: import.meta.env.VITE_ENVIRONMENT || 'development',
  release: getAppVersion(),
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  replaysSessionSampleRate: isProd ? 0.1 : 0,
  replaysOnErrorSampleRate: isProd ? 1.0 : 0,
  tracesSampleRate: isProd ? 0.1 : 0,
});

if (isProd) {
  // Removes console logs in production for better performance, security, and cleaner output
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
  // console.error = () => { }; // Keep errors for visibility
}

createRoot(document.getElementById('root')!).render(
  // <StrictMode>
  <App />
  // </StrictMode>
);

registerDebugTools();
window.addEventListener('beforeunload', () => jitsiService?.destroy());
registerActivityCleanExit();
registerChatTracking();
export default {};
